import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Award, Mail } from "lucide-react";
import SEO from "../components/SEO";
import "./BlogArticle.css";

export const TaxPlanningGrowingBusinessUK = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Tax Planning Strategies for Growing Businesses (UK 2026)",
    "description": "Strategic tax planning for growing UK businesses. Reduce Corporation Tax legally while scaling profitably.",
    "author": {
      "@type": "Organization",
      "name": "Brightson Accounting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Brightson Accounting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://brightsonaccounting.co.uk/logo.png"
      }
    },
    "datePublished": "2026-04-10",
    "dateModified": "2026-04-10"
  };

  return (
    <div className="article-page">
      <SEO
        title="Tax Planning Strategies for Growing Businesses (UK 2026)"
        description="Strategic tax planning for growing UK businesses. Reduce Corporation Tax legally while scaling profitably."
        canonical="/blog/tax-planning-growing-business-uk"
        keywords="tax planning UK, reduce corporation tax, business tax planning, tax strategies UK, Wolverhampton accountant"
        schema={JSON.stringify(articleSchema)}
      />

      <section className="article-header">
        <div className="container">
          <div className="article-back">
            <Link to="/blog" className="back-link">
              <ArrowLeft size={20} />
              Back to Blog
            </Link>
          </div>
          <div className="article-header-content">
            <span className="article-category-badge">Growing your company</span>
            <h1 className="article-main-title">Tax Planning Strategies for Growing Businesses (UK 2026)</h1>
            <p className="article-subtitle">Reduce Tax Legally from Brightson Accounting, Wolverhampton</p>
            <div className="article-meta-bar">
              <div className="meta-author">
                <Award size={18} />
                <span>Brightson Accounting (FCCA Qualified)</span>
              </div>
              <div className="meta-info">
                <Calendar size={16} />
                <span>10 April 2026</span>
                <Clock size={16} />
                <span>9 min read</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="article-content">
        <div className="container">
          <div className="article-body">
            
            {/* Traffic boost hook */}
            <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '6px', padding: '1rem', marginBottom: '2rem' }}>
              <p style={{ margin: 0, fontSize: '0.95rem', color: '#92400e' }}>
                Most business owners overpay tax — <Link to="/corporation-tax-calculator" style={{ color: '#92400e', fontWeight: 600, textDecoration: 'underline' }}>see how much you could save using our calculator 👉</Link>
              </p>
            </div>

            <p className="article-lead">
              The bigger your profit, the bigger your tax bill — unless you plan ahead.
            </p>

            <p>
              Strategic tax planning can save growing businesses thousands of pounds every year — completely legally.
            </p>

            <p>
              At <Link to="/services">Brightson Accounting in Wolverhampton</Link>, we help businesses across the West Midlands reduce tax while staying 100% HMRC-compliant.
            </p>

            <div className="info-box">
              <strong>Quick Summary</strong>
              <ul>
                <li>Plan tax annually, not at year-end</li>
                <li>Use salary + dividends to optimize extraction</li>
                <li>Claim all allowable expenses and capital allowances</li>
                <li>Contribute to pensions to reduce Corporation Tax</li>
                <li>Time income and expenses strategically</li>
              </ul>
            </div>

            <h2>Why Tax Planning Matters</h2>

            <p>
              Most business owners only think about tax when filing returns — but by then, it's too late to optimize.
            </p>

            <p>
              Effective tax planning means making decisions throughout the year that reduce your liability legally.
            </p>

            <h2>Strategy 1: Optimize Salary vs Dividends</h2>

            <p>
              For limited company directors, the most tax-efficient way to extract income is a combination of salary and dividends.
            </p>

            <h3>Optimal Strategy (2026/27):</h3>

            <ul>
              <li><strong>Salary:</strong> £12,570 (tax-free Personal Allowance)</li>
              <li><strong>Dividends:</strong> Everything else</li>
            </ul>

            <p>
              Why?
            </p>

            <ul>
              <li>Salary up to £12,570 has no Income Tax or NI</li>
              <li>Dividends are taxed at lower rates than salary (8.75% vs 20%)</li>
              <li>Dividends don't attract National Insurance</li>
            </ul>

            <p>
              Full guide: <Link to="/blog/salary-vs-dividends-uk-2026">Salary vs Dividends: What's Best for Directors in 2026?</Link>
            </p>

            <div className="info-box" style={{ background: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
              <p style={{ margin: 0 }}>
                <strong>💡 Want to see how much tax you could save?</strong><br/><br/>
                Most business owners we speak to are overpaying without realising it.<br/><br/>
                <Link to="/corporation-tax-calculator" style={{ color: '#4caf50', fontWeight: 600 }}>
                  👉 Try the Corporation Tax Calculator
                </Link>
              </p>
            </div>

            <h2>Strategy 2: Claim All Allowable Expenses</h2>

            <p>
              Every pound of allowable expenses reduces your taxable profit — and saves you 19-25% in Corporation Tax.
            </p>

            <h3>Commonly Missed Expenses:</h3>

            <ul>
              <li>Home office costs (if you work from home)</li>
              <li>Business mileage (45p per mile for first 10,000 miles)</li>
              <li>Professional subscriptions and memberships</li>
              <li>Training and courses</li>
              <li>Software and subscriptions</li>
              <li>Marketing and advertising</li>
              <li>Bank charges and loan interest</li>
            </ul>

            <p>
              From what we see with clients in Wolverhampton, most businesses miss £2,000-£5,000 in allowable expenses every year.
            </p>

            <h2>Strategy 3: Use Capital Allowances</h2>

            <p>
              When you buy equipment, vehicles, or machinery, you can claim capital allowances — often 100% in year 1.
            </p>

            <h3>Annual Investment Allowance (AIA):</h3>

            <p>
              Claim 100% of the cost (up to £1 million) in the first year.
            </p>

            <p>
              Example:
            </p>

            <ul>
              <li>Buy a £20,000 van</li>
              <li>Claim £20,000 as a deduction</li>
              <li>Save £3,800 in Corporation Tax (19%)</li>
            </ul>

            <h2>Strategy 4: Contribute to Pensions</h2>

            <p>
              Employer pension contributions are one of the most tax-efficient strategies available.
            </p>

            <p>
              Benefits:
            </p>

            <ul>
              <li>Reduces Corporation Tax (19-25%)</li>
              <li>Builds long-term wealth</li>
              <li>No limit on employer contributions (must be "reasonable")</li>
            </ul>

            <p>
              Example:
            </p>

            <ul>
              <li>Company contributes £10,000 to your pension</li>
              <li>Saves £1,900-£2,500 in Corporation Tax</li>
              <li>You pay no Income Tax or NI on the contribution</li>
            </ul>

            <p>
              Many business owners we work with in Birmingham use pensions to smooth out high-profit years.
            </p>

            <h2>Strategy 5: Time Income and Expenses</h2>

            <p>
              You can reduce tax by timing when income is recognized and expenses are paid.
            </p>

            <h3>Strategies:</h3>

            <ul>
              <li>Delay invoicing until after year-end (if cash flow allows)</li>
              <li>Bring forward expenses into the current year</li>
              <li>Prepay next year's expenses (software, rent, insurance)</li>
              <li>Time large asset purchases for maximum tax benefit</li>
            </ul>

            <p className="highlight-note">
              This is legal tax planning — not avoidance. You're simply controlling the timing within HMRC rules.
            </p>

            <h2>Strategy 6: Use R&D Tax Credits</h2>

            <p>
              If you're innovating or developing new products/processes, you may qualify for R&D tax credits.
            </p>

            <p>
              You can claim:
            </p>

            <ul>
              <li>Up to 86% of qualifying R&D costs (for loss-making companies)</li>
              <li>Or an enhanced deduction on Corporation Tax</li>
            </ul>

            <p>
              Common qualifying activities:
            </p>

            <ul>
              <li>Developing new software or apps</li>
              <li>Improving manufacturing processes</li>
              <li>Creating new products</li>
              <li>Solving technical challenges</li>
            </ul>

            <h2>Strategy 7: Manage VAT Strategically</h2>

            <p>
              If you're close to the VAT threshold (£90,000), consider:
            </p>

            <ul>
              <li>Registering voluntarily to reclaim VAT on purchases</li>
              <li>Using the Flat Rate Scheme to simplify admin</li>
              <li>Planning purchases before registration to claim back VAT</li>
            </ul>

            <p>
              Full guide: <Link to="/blog/vat-planning-uk">VAT Planning Strategies for Growing Businesses</Link>
            </p>

            <h2>Strategy 8: Review Your Business Structure</h2>

            <p>
              As profits grow, your business structure matters more.
            </p>

            <p>
              Sole traders pay higher tax rates than limited companies once profits exceed £30,000-£50,000.
            </p>

            <p>
              Read: <Link to="/blog/when-to-go-limited-uk">When Should You Switch to a Limited Company?</Link>
            </p>

            <h2>Common Tax Planning Mistakes</h2>

            <p>
              We see the same errors with growing businesses:
            </p>

            <ul>
              <li>Waiting until year-end to think about tax</li>
              <li>Not keeping receipts for expenses</li>
              <li>Missing capital allowance claims</li>
              <li>Taking all profit as salary (instead of dividends)</li>
              <li>Not using pension contributions</li>
            </ul>

            <h2>When to Get Professional Help</h2>

            <p>
              As your business grows, tax planning becomes more complex — and more valuable.
            </p>

            <p>
              An <Link to="/services">accountant in Wolverhampton</Link> can:
            </p>

            <ul>
              <li>Model different scenarios to minimize tax</li>
              <li>Ensure you claim all available reliefs</li>
              <li>Keep you HMRC-compliant</li>
              <li>Save you far more than they cost</li>
            </ul>

            <p>
              Read: <Link to="/blog/when-to-hire-accountant-uk">When Should You Hire an Accountant?</Link>
            </p>

            <h2>Tax Planning in Wolverhampton & the West Midlands</h2>

            <p>
              At Brightson Accounting, we help local businesses:
            </p>

            <ul>
              <li>Reduce Corporation Tax legally</li>
              <li>Plan salary and dividend extraction</li>
              <li>Claim all allowable expenses and reliefs</li>
              <li>Stay 100% HMRC-compliant</li>
            </ul>

            <div className="article-cta-box">
              <h2>Ready to Reduce Your Tax Bill?</h2>
              <p>
                We help businesses across Wolverhampton and the West Midlands reduce tax legally, improve cash flow, and stay compliant.
              </p>
              <Link to="/contact" className="article-cta-button">
              <Mail size={20} />
                Book a Free Consultation
              </Link>
            </div>

            <div className="disclaimer">
              <h3>Disclaimer</h3>
              <p>
                This content is for general guidance only. For tailored advice, contact Brightson Accounting.
              </p>
            </div>

          </div>

          <aside className="article-sidebar">
            <div className="sidebar-card">
              <h3 className="sidebar-heading">Need Help?</h3>
              <p>Speak to our FCCA-qualified accountants about tax planning.</p>
              <Link to="/contact" className="sidebar-button">
                <Mail size={18} />
                Contact Us
              </Link>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-heading">Related Articles</h3>
              <div className="related-links">
                <Link to="/blog/salary-vs-dividends-uk-2026" className="related-link">Salary vs Dividends</Link>
                <Link to="/blog/when-to-hire-accountant-uk" className="related-link">When to Hire Accountant</Link>
                <Link to="/blog/scale-business-uk" className="related-link">Scale Your Business</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default TaxPlanningGrowingBusinessUK;
